// SearchBar.js — v2 : recherche par titre, description ET date

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CATEGORIES, formatYear } from '../data/events';

// Détecte si la requête est une année ou une plage d'années
// Ex: "1492", "-44", "44 av", "1500-1600", "XVe siècle"
function parseYearQuery(q) {
  const s = q.trim();

  // Plage : "1500-1600" ou "1500 1600"
  const range = s.match(/^(-?\d+)\s*[-–]\s*(-?\d+)$/);
  if (range) return { type: 'range', min: parseInt(range[1]), max: parseInt(range[2]) };

  // "44 av" ou "44 av. J.-C." ou "44bc"
  const bc = s.match(/^(\d+)\s*(av|bc|avant|BCE)/i);
  if (bc) return { type: 'exact', year: -parseInt(bc[1]), tolerance: 20 };

  // Nombre simple : "1492" ou "-44"
  const num = s.match(/^-?\d{1,4}$/);
  if (num) return { type: 'exact', year: parseInt(s), tolerance: 15 };

  // Siècle romain ou numérique : "XVe" "15e" "15ème" "XVème"
  const century = s.match(/^(X{0,3}(?:IX|IV|V?I{0,3})|1?\d)(e|ème|eme|st|nd|rd|th)?\s*(siècle|s\.?|century)?$/i);
  if (century) {
    const roman = { I:1,V:5,X:10,L:50,C:100,D:500,M:1000 };
    let n = parseInt(century[1]);
    if (isNaN(n)) {
      // Convertir romain
      const r = century[1].toUpperCase();
      n = 0;
      for (let i = 0; i < r.length; i++) {
        const cur = roman[r[i]] || 0, nxt = roman[r[i+1]] || 0;
        n += cur < nxt ? -cur : cur;
      }
    }
    if (n >= 1 && n <= 21) {
      return { type: 'range', min: (n - 1) * 100, max: n * 100 - 1 };
    }
  }

  return null;
}

function matchesYear(event, yearQuery) {
  if (!yearQuery) return false;
  if (yearQuery.type === 'range') {
    return event.year >= yearQuery.min && event.year <= yearQuery.max;
  }
  if (yearQuery.type === 'exact') {
    return Math.abs(event.year - yearQuery.year) <= yearQuery.tolerance;
  }
  return false;
}

export function SearchBar({ allEvents, onSelect, onYearChange }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [focused, setFocused] = useState(false);
  const [yearHint, setYearHint] = useState(null);
  const inputRef = useRef(null);

  const search = useCallback((q) => {
    if (!q.trim()) { setResults([]); setYearHint(null); return; }

    const lower    = q.toLowerCase();
    const yearQ    = parseYearQuery(q);
    setYearHint(yearQ);

    const scored = allEvents.map(ev => {
      let score = 0;
      const titleL = ev.title.toLowerCase();
      const descL  = ev.description?.toLowerCase() || '';

      // Correspondance titre exacte
      if (titleL === lower) score += 100;
      else if (titleL.startsWith(lower)) score += 50;
      else if (titleL.includes(lower)) score += 20;

      // Correspondance description
      if (descL.includes(lower)) score += 5;

      // Correspondance formatYear (ex: "1492 ap. J.-C.")
      if (formatYear(ev.year).toLowerCase().includes(lower)) score += 15;

      // Correspondance année
      if (yearQ && matchesYear(ev, yearQ)) {
        score += yearQ.type === 'exact'
          ? Math.max(40, 60 - Math.abs(ev.year - yearQ.year) * 2)
          : 30;
      }

      return { ev, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 10)
    .map(({ ev }) => ev);

    setResults(scored);
  }, [allEvents]);

  const handleChange = useCallback((e) => {
    setQuery(e.target.value);
    search(e.target.value);
  }, [search]);

  const handleSelect = useCallback((event) => {
    setQuery(event.title);
    setResults([]);
    setYearHint(null);
    onYearChange(event.year);
    setTimeout(() => onSelect(event), 300);
  }, [onSelect, onYearChange]);

  // Naviguer directement à une année si c'est une requête purement numérique
  const handleYearJump = useCallback(() => {
    if (!yearHint) return;
    const targetYear = yearHint.type === 'exact'
      ? yearHint.year
      : Math.round((yearHint.min + yearHint.max) / 2);
    onYearChange(targetYear);
    setResults([]);
    setFocused(false);
    inputRef.current?.blur();
  }, [yearHint, onYearChange]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setResults([]); setQuery(''); setYearHint(null);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const isYearOnly = yearHint && results.length === 0;

  return (
    <div className={`search-bar ${focused ? 'focused' : ''}`}>
      <div className="search-input-wrap">
        <span className="search-icon">🔍</span>
        <input
          ref={inputRef}
          type="text"
          placeholder="Titre, année, siècle… (⌘K)"
          value={query}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          className="search-input"
        />
        {query && (
          <button className="search-clear" onClick={() => { setQuery(''); setResults([]); setYearHint(null); }}>×</button>
        )}
      </div>

      {focused && (results.length > 0 || isYearOnly) && (
        <div className="search-results">

          {/* Bouton "Aller à l'année" si requête date */}
          {yearHint && (
            <button className="search-year-jump" onMouseDown={handleYearJump}>
              <span className="year-jump-icon">📅</span>
              <span className="year-jump-label">
                {yearHint.type === 'range'
                  ? `Aller à ${formatYear(yearHint.min)} — ${formatYear(yearHint.max)}`
                  : `Aller à ${formatYear(yearHint.year)}`}
              </span>
              <span className="year-jump-arrow">→</span>
            </button>
          )}

          {/* Résultats événements */}
          {results.map(event => {
            const cat = CATEGORIES[event.category];
            return (
              <button key={event.id} className="search-result-item" onMouseDown={() => handleSelect(event)}>
                <span className="result-icon">{cat.icon}</span>
                <div className="result-text">
                  <span className="result-title">{event.title}</span>
                  <span className="result-year" style={{ color: cat.color }}>
                    {formatYear(event.year)}
                  </span>
                </div>
              </button>
            );
          })}

          {/* Message si aucun résultat texte mais date reconnue */}
          {isYearOnly && (
            <div className="search-no-event">
              Aucun événement trouvé — utilisez le bouton ci-dessus pour naviguer à cette date.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
